/**
 * Keeps React running when a browser page translator (Chrome / Google Translate) moved our text
 * nodes. The translator wraps a text node in its own `<font>` element. React still holds the old
 * node, and its next `removeChild` or `insertBefore` throws `NotFoundError`: the error boundary
 * then shows the crash page. Seen on the welcome page and on the new song page with a zh-TW
 * browser (MUSIC24-WEB-3, MUSIC24-WEB-7).
 *
 * With the guard, a call on a node that has another parent does nothing, and React goes on. The
 * translated text can stay stale until the next render; a crash page is worse. The same guard is
 * the workaround in https://github.com/facebook/react/issues/11538.
 */
export function guardDomMutations(): void {
  if (typeof Node !== 'function' || Node.prototype === undefined) {
    return;
  }
  const proto = Node.prototype as Node & { m24DomGuard?: boolean };
  if (proto.m24DomGuard === true) {
    return;
  }
  proto.m24DomGuard = true;

  // Read through the descriptor: these run later with `call(this, ...)`, never as a loose method.
  const originalRemoveChild = Object.getOwnPropertyDescriptor(proto, 'removeChild')?.value as (
    this: Node,
    child: Node
  ) => Node;
  proto.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Object.getOwnPropertyDescriptor(proto, 'insertBefore')?.value as (
    this: Node,
    node: Node,
    child: Node | null
  ) => Node;
  proto.insertBefore = function insertBefore<T extends Node>(this: Node, node: T, child: Node | null): T {
    if (child !== null && child.parentNode !== this) {
      return node;
    }
    return originalInsertBefore.call(this, node, child) as T;
  };
}
